import { isSupabaseConfigured, supabase } from '../lib/supabase.js'

const requireSupabase = () => {
  if (!isSupabaseConfigured) throw new Error('Supabase não configurado.')
}

const IN_QUERY_BATCH_SIZE = 75

async function selectByMessageIds(table, ids) {
  const rows = []
  for (let index = 0; index < ids.length; index += IN_QUERY_BATCH_SIZE) {
    const batch = ids.slice(index, index + IN_QUERY_BATCH_SIZE)
    const { data, error } = await supabase.from(table).select('*').in('message_id', batch)
    if (error) throw error
    rows.push(...(data || []))
  }
  return rows
}

export async function loadWhatsAppWorkspace(organizationId, users = []) {
  requireSupabase()
  const [channelsResult, contactsResult, conversationsResult, labelsResult, linksResult] = await Promise.all([
    supabase.from('whatsapp_channels').select('*').eq('organization_id', organizationId).eq('active', true),
    supabase.from('whatsapp_contacts').select('*').eq('organization_id', organizationId),
    supabase.from('whatsapp_conversations').select('*').eq('organization_id', organizationId).order('last_message_at', { ascending: false, nullsFirst: false }),
    supabase.from('whatsapp_labels').select('*').eq('organization_id', organizationId).eq('active', true).order('name'),
    supabase.from('whatsapp_conversation_labels').select('conversation_id, label_id').eq('organization_id', organizationId),
  ])
  const error = channelsResult.error || contactsResult.error || conversationsResult.error || labelsResult.error || linksResult.error
  if (error) throw error
  const avatarUrls = {}
  await Promise.all((contactsResult.data || []).map(async contact => {
    if (!contact.avatar_storage_bucket || !contact.avatar_storage_path) return
    const { data } = await supabase.storage.from(contact.avatar_storage_bucket).createSignedUrl(contact.avatar_storage_path, 3600)
    if (data?.signedUrl) avatarUrls[contact.id] = data.signedUrl
  }))
  const contacts = Object.fromEntries((contactsResult.data || []).map(item => [item.id, item]))
  const owners = Object.fromEntries(users.map(item => [item.id, item.full_name]))
  const conversations = (conversationsResult.data || []).map(item => {
    const contact = contacts[item.contact_id] || {}
    return {
      id: item.id,
      name: contact.name || contact.phone_e164 || 'Contato',
      phone: contact.phone_e164 || '',
      avatarUrl: avatarUrls[contact.id] || contact.avatar_url || '',
      preview: item.last_message_preview || 'Conversa iniciada',
      lastMessageAt: item.last_message_at || null,
      time: item.last_message_at ? new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(new Date(item.last_message_at)) : '',
      unread: item.unread_count || 0,
      status: item.status,
      owner: owners[item.assigned_to] || 'Sem responsável',
      ownerId: item.assigned_to,
      online: false,
      archived: Boolean(item.archived_at),
      muted: item.muted,
      priority: item.priority,
      requestedService: item.requested_service || '',
      requestedServiceId: item.requested_service_id || '',
      leadId: item.lead_id,
      contactId: item.contact_id,
      channelId: item.channel_id,
    }
  })
  const conversationLabels = {}
  for (const link of linksResult.data || []) (conversationLabels[link.conversation_id] ||= []).push(link.label_id)
  return { channels: channelsResult.data || [], conversations, labels: labelsResult.data || [], conversationLabels }
}

export async function loadWhatsAppMessages(conversationId) {
  requireSupabase()
  const messagesResult = await supabase.from('whatsapp_messages').select('*').eq('conversation_id', conversationId).order('received_at')
  if (messagesResult.error) throw messagesResult.error
  const ids = (messagesResult.data || []).map(item => item.id)
  const [reactionRows, attachmentRows] = ids.length ? await Promise.all([
    selectByMessageIds('whatsapp_message_reactions', ids),
    selectByMessageIds('whatsapp_message_attachments', ids),
  ]) : [[], []]
  const byId = Object.fromEntries((messagesResult.data || []).map(item => [item.id, item]))
  const reactions = Object.fromEntries(reactionRows.map(item => [item.message_id, item.emoji]))
  const attachments = Object.fromEntries(attachmentRows.map(item => [item.message_id, item]))
  const signedUrls = {}
  await Promise.all(attachmentRows.map(async attachment => {
    if (!attachment.storage_bucket || !attachment.storage_path) return
    const { data } = await supabase.storage.from(attachment.storage_bucket).createSignedUrl(attachment.storage_path, 3600)
    if (data?.signedUrl) signedUrls[attachment.message_id] = data.signedUrl
  }))
  return (messagesResult.data || []).map(item => {
    const attachment = attachments[item.id] || {}
    return {
      id: item.id,
      direction: item.direction === 'inbound' ? 'in' : 'out',
      type: item.type,
      text: item.body,
      name: attachment.file_name,
      size: attachment.file_size ? `${Math.ceil(attachment.file_size / 1024)} KB` : undefined,
      mediaUrl: signedUrls[item.id] || attachment.external_url,
      address: item.type === 'location' ? item.body : undefined,
      time: new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(new Date(item.sent_at || item.received_at || item.created_at)),
      status: item.status,
      reaction: reactions[item.id],
      quoted: item.quoted_message_id ? byId[item.quoted_message_id]?.body || 'Mensagem citada' : null,
      quoted_message_id: item.quoted_message_id,
      external_message_id: item.external_message_id,
      editedAt: item.edited_at,
      deletedAt: item.deleted_at,
      raw: item,
    }
  })
}

export async function sendWhatsAppMessage(conversationId, text, quotedMessageId = null) {
  requireSupabase()
  const { data, error } = await supabase.functions.invoke('whatsapp-send', { body: { conversation_id: conversationId, text, quoted_message_id: quotedMessageId } })
  if (error) throw error
  if (!data?.ok) throw new Error(data?.error || 'Não foi possível enviar a mensagem.')
  return data.message
}

export async function runWhatsAppAction(conversationId, action, payload = {}) {
  requireSupabase()
  if (action === 'mark_read') {
    const { error } = await supabase.rpc('mark_whatsapp_conversation_read', { p_conversation_id: conversationId })
    if (error) throw error
    return { ok: true, action }
  }
  if (action === 'archive' || action === 'restore') {
    const { data: authData, error: authError } = await supabase.auth.getUser()
    if (authError || !authData.user) throw new Error('Sua sessão expirou. Entre novamente para continuar.')
    const changes = action === 'archive'
      ? { archived_at: new Date().toISOString(), archived_by: authData.user.id }
      : { archived_at: null, archived_by: null }
    const { error } = await supabase.from('whatsapp_conversations').update(changes).eq('id', conversationId)
    if (error) throw error
    return { ok: true, action }
  }
  if (action === 'add_label' || action === 'remove_label') {
    const labelId = String(payload.label_id || '')
    if (!labelId) throw new Error('Etiqueta inválida.')
    if (action === 'add_label') {
      const { data: conversation, error: conversationError } = await supabase.from('whatsapp_conversations').select('organization_id').eq('id', conversationId).single()
      if (conversationError) throw conversationError
      const { data: authData, error: authError } = await supabase.auth.getUser()
      if (authError || !authData.user) throw new Error('Sua sessão expirou. Entre novamente para continuar.')
      const { error } = await supabase.from('whatsapp_conversation_labels').insert({ organization_id: conversation.organization_id, conversation_id: conversationId, label_id: labelId, created_by: authData.user.id })
      if (error && error.code !== '23505') throw error
    } else {
      const { error } = await supabase.from('whatsapp_conversation_labels').delete().eq('conversation_id', conversationId).eq('label_id', labelId)
      if (error) throw error
    }
    return { ok: true, action }
  }
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession()
  if (sessionError || !sessionData.session?.access_token) throw new Error('Sua sessão expirou. Entre novamente para continuar.')
  const baseUrl = String(import.meta.env.VITE_SUPABASE_URL || '').replace(/\/+$/, '')
  const publishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY
  let response
  try {
    response = await fetch(`${baseUrl}/functions/v1/whatsapp-actions`, {
      method: 'POST',
      headers: {
        apikey: publishableKey,
        Authorization: `Bearer ${sessionData.session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ conversation_id: conversationId, action, ...payload }),
    })
  } catch {
    throw new Error('Não foi possível acessar o serviço do WhatsApp. Verifique a conexão e tente novamente.')
  }
  const raw = await response.text()
  let data = null
  try { data = raw ? JSON.parse(raw) : null } catch { data = null }
  if (!response.ok || !data?.ok) {
    if (response.status === 401) throw new Error('Sua sessão expirou. Entre novamente para continuar.')
    throw new Error(data?.error || `Não foi possível executar a ação (${response.status}).`)
  }
  return data
}

export function subscribeToWhatsApp(organizationId, onChange) {
  if (!isSupabaseConfigured) return () => {}
  const tables = ['whatsapp_channels', 'whatsapp_contacts', 'whatsapp_conversations', 'whatsapp_messages', 'whatsapp_message_attachments', 'whatsapp_message_reactions', 'whatsapp_labels', 'whatsapp_conversation_labels']
  const channel = supabase.channel(`crm-whatsapp-${organizationId}`)
  for (const table of tables) channel.on('postgres_changes', { event: '*', schema: 'public', table, filter: `organization_id=eq.${organizationId}` }, payload => onChange(table, payload))
  channel.subscribe()
  return () => supabase.removeChannel(channel)
}
